import React, { useCallback, useState, useRef } from 'react';
import { useBlockingRouter } from '../../../hooks/useBlockingRouter';
import { SignUpPage_GetInvitationWhereQuery, InviteEnum } from '#types';
import { FormControl, InputLabel, Input } from '@mui/material';
import styles from './CreateCoreUser.module.scss';
import validations from '../../../lib/helpers/validations';
import { beginUserRegistration } from '../../../redux/actions';
import { OrgOnboardingSteps } from '../../OrgOnboarding/OrgOnboarding';
import { Button } from '../../../blocks/Button';
import { Password } from '../../Password';
import { useDispatch } from 'react-redux';
import { ClientOnboardingSteps } from '../../ClientOnboarding';

export type CreateCoreUserProps = {
	email?: string;
	invitation?: SignUpPage_GetInvitationWhereQuery['getInvitationWhere'];
	onSuccess: () => void;
	password?: string;
	confirmPassword?: string;
	setPassword: ( password: string ) => void;
	setConfirmPassword: ( confirmPassword: string ) => void;
};

export type Validation =
	| {
			password?: string;
			confirmPassword?: string;
	  }
	| undefined;

export const CreateCoreUser: React.FC<CreateCoreUserProps> = ( {
	email,
	invitation,
	onSuccess,
	password,
	confirmPassword,
	setPassword,
	setConfirmPassword,
} ) => {
	const [ validation, setValidation ] = useState<Validation>( undefined );
	const validationEnabledRef = useRef( false );
	const dispatch = useDispatch();

	const router = useBlockingRouter();

	const onValidate = useCallback(
		( force: boolean ) => {
			if ( !force && !validationEnabledRef.current ) {
				return true;
			}
			validationEnabledRef.current = true;
			const {
				password: passwordValidation,
				repassword: confirmPasswordValidation,
			} = validations.validatePasswords( password, confirmPassword );
			const newValidation = {
				...( passwordValidation ? { password: passwordValidation } : {} ),
				...( confirmPasswordValidation
					? { confirmPassword: confirmPasswordValidation }
					: {} ),
			};
			setValidation( newValidation );
			return !Object.keys( newValidation ).length;
		},
		[ password, confirmPassword ]
	);

	const handleSubmit = useCallback(
		async ( e: React.FormEvent<HTMLFormElement> ) => {
			e.preventDefault();
			if ( !onValidate( true ) ) {
				return;
			}
			if ( invitation ) {
				dispatch(
					beginUserRegistration( {
						coreUserFields: {
							email,
							password,
							repassword: confirmPassword,
						},
						invitation,
					} )
				);
				if ( invitation.invitationType === InviteEnum.NewOrg ) {
					router.push( `/orgOnboarding/${ OrgOnboardingSteps.YourInfo }` );
					return;
				} else if ( invitation.invitationType === InviteEnum.NewClient ) {
					router.push( `/clientOnboarding/${ ClientOnboardingSteps.YourInfo }` );
					return;
				} else if ( invitation.invitationType === InviteEnum.NewOrgMember ) {
					router.push( '/OrgMemberSignUp/' + 1 );
					return;
				} else {
					// should never get here
					return;
				}
			} else {
				onSuccess();
			}
		},
		[
			email,
			password,
			invitation,
			onValidate,
			onSuccess,
			confirmPassword,
			router,
			dispatch,
		]
	);

	return (
		<form className={ styles.form } onSubmit={ handleSubmit } noValidate>
			<FormControl variant='standard' className={ styles.input }>
				<InputLabel htmlFor='email' shrink>
					Email Address
				</InputLabel>
				<Input
					id='email'
					data-cy='email-field'
					slotProps={ { input: { type: 'email', inputMode: 'email' } } }
					value={ email }
					disabled={ true }
				/>
			</FormControl>
			<Password
				className={ styles.input }
				label='Password'
				value={ password }
				variant='standard'
				onChange={ ( e ) => setPassword( e.target.value ) }
				error={ !!validation?.password }
				onBlur={ () => onValidate( false ) }
				errorText={ validation?.password }
			/>
			<Password
				className={ styles.input }
				label='Confirm password'
				dataCy='confirmPassword-field'
				value={ confirmPassword }
				variant='standard'
				onChange={ ( e ) => setConfirmPassword( e.target.value ) }
				error={ !!validation?.confirmPassword }
				onBlur={ () => onValidate( false ) }
				errorText={ validation?.confirmPassword }
			/>
			<Button
				className={ styles.submitButton }
				data-cy='submit-button'
				type='submit'
				color='primary'
				variant='contained'
			>
				Sign up
			</Button>
		</form>
	);
};
